// © Microsoft Corporation. All rights reserved.

import { BaseExperience, InitialCommonExperienceProps } from "@msnews/experiences-base";
import {
    ContentActionsToolbarHandledProps,
    ContentActionsToolbarUnhandledProps
} from "./ContentActionsToolbar.props";
import { ContentActionsToolbarProps, ToolbarPosition } from ".";
import { PlaceHolderId, experienceDefaultName } from "./ContentActionsToolbar.constants";

import { ContentActionsToolbarAppErrors } from "@msnews/diagnostics";
import { HandledPropsKeys } from "@msnews/core";
import React from "react";
import { SharingToolbar } from "./components/sharing-toolbar";
import { Telemetry } from "@msnews/telemetry-contracts";
import { classNames } from "@microsoft/fast-web-utilities";
import { markTtvr } from "@msnews/diagnostics";

/**
 * Content Action toolbar base experience class
 * @class - ContentActionsToolbar
 * @classdesc - ContentActionsToolbar
 */
export class ContentActionsToolbar extends BaseExperience<ContentActionsToolbarHandledProps, ContentActionsToolbarUnhandledProps, {}> {
    /**
     * The ContentActionsToolbar default prop contract
     * Describes any default values for any component handled props
     * @public
     * @type { Partial<ContentActionsToolbarHandledProps> }
     */
    public static defaultProps: Partial<ContentActionsToolbarHandledProps> = {
        experienceName: experienceDefaultName
    };

    /**
     * The content action toolbar handled prop contract
     * Describes the object that enumerates all handled props for a component. This
     * object includes all props that can in some way consumed or manipulated by component
     * code.
     * @protected
     * @type { HandledPropsKeys<ContentActionsToolbarHandledProps> }
     */
    protected handledProps: HandledPropsKeys<ContentActionsToolbarHandledProps> = {
        ...InitialCommonExperienceProps,
        contentStatus: undefined,
        managedClasses: undefined,
        position: undefined,
        rootTelemetryTags: undefined,
        sharingError: undefined,
        sharingToolbar: undefined,
        staticPosition: undefined
    };

    /**
     * Flag to confirm first render is completed
     * @type {boolean}
     */
    private firstRenderCompleted = false;

    /**
     * Constructs a ContentActionsToolbar component
     * @constructor
     * @param { ContentActionsToolbarProps } props props contract
     */
    constructor(props: ContentActionsToolbarProps) {
        super(props);
    }

    /**
     * Render App component.
     *
     * @public
     * @return {JSX.Element}
     */
    public render(): JSX.Element {
        super.render();

        if (this.props.position && this.props.sharingToolbar && this.props.sharingToolbar.sharingButtons && !this.props.sharingError) {
            return (
                <div {...this.unhandledProps}
                    className={this.generateClassNames(classNames(this.props.managedClasses.contentActionsToolbar, this.getPositioningClassName()))}
                >
                    <SharingToolbar
                        {...this.props.sharingToolbar}
                        {...this.props.rootTelemetryTags}
                    />
                </div>
            );
        }
        return null;
    }

    /**
     * Trigger after component update
     */
    public componentDidUpdate(prevProps: ContentActionsToolbarHandledProps): void {
        super.componentDidUpdate(prevProps);

        if (!prevProps.staticPosition && this.shouldFlip()) {
            this.flipToolbar();
        }

        if (!this.firstRenderCompleted) {
            markTtvr(this.props.experienceName);
            this.firstRenderCompleted = true;
        }
    }

    /**
     * A handler that gets invoked for unhandled errors.
     * @param error error object.
     */
    public componentDidCatch(error: Error): void {
        const appError = { ...ContentActionsToolbarAppErrors.ContentActionsBaseExperienceError, message: `${error.message}\n${error.stack}` };
        Telemetry.sendAppErrorEvent(appError);
    }

    /**
     * Get the classname based on positioning of the toolbar
     */
    private getPositioningClassName(): string {
        const managedClasses = this.props.managedClasses;
        const positionClass: string = this.props.position === ToolbarPosition.horizontal ?
            managedClasses.contentActionsToolbar_horizontal :
            managedClasses.contentActionsToolbar_vertical;

        return positionClass;
    }

    /**
     * Check if toolbar needs to flip from one position to another
     * @method
     * @returns {boolean} True if toolbar should flip. False otherwise.
     */
    private shouldFlip(): boolean {
        const { position } = this.props;

        const currentHostId = position === ToolbarPosition.vertical ? PlaceHolderId.vertical : PlaceHolderId.horizontal;
        const hostElem = document.getElementById(currentHostId);
        if (!hostElem) {
            Telemetry.sendAppErrorEvent({
                ...ContentActionsToolbarAppErrors.ContentActionsFlipToolbarError,
                message: "Missing toolbar placeholder while checking shouldFlip",
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsFlipToolbarError.pb,
                    customMessage: `CurrentHostId: ${currentHostId}`
                }
            });
            return false;
        }
        return hostElem.getElementsByTagName("ul").length === 0;
    }

    /**
     * Move toolbar from one placeholder to another, depending on the toolbar position
     * @method
     */
    private flipToolbar(): void {
        const { position } = this.props;
        const prevHostId = position === ToolbarPosition.vertical ? PlaceHolderId.horizontal : PlaceHolderId.vertical;
        const newHostId = position === ToolbarPosition.vertical ? PlaceHolderId.vertical : PlaceHolderId.horizontal;

        const prevHostElem = document.getElementById(prevHostId);
        const newHostElem = document.getElementById(newHostId);

        if (!prevHostElem || !newHostElem) {
            return null;
        }

        while (prevHostElem.childNodes.length > 0) {
            newHostElem.appendChild(prevHostElem.childNodes[0]);
        }
    }
}